// Grava os dois lados da ligação em separado (atendente e cliente) para a
// transcrição saber quem falou o quê. A cada poucos minutos fecha um trecho e
// envia: cada trecho é um arquivo completo (o MediaRecorder só põe cabeçalho
// no começo da gravação) e pequeno o bastante para qualquer limite de upload.
// Nada fica guardado no navegador; o backend apaga o áudio depois de transcrever.

const CHUNK_MS = 4 * 60 * 1000;
const BITRATE = 24000;
// Trecho menor que isso é cabeçalho sem fala: não vale o envio.
const MIN_BYTES = 2000;

function pickMime() {
  if (!window.MediaRecorder?.isTypeSupported) return null;
  return (
    ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm"].find((type) =>
      window.MediaRecorder.isTypeSupported(type),
    ) || null
  );
}

export function transcriptionSupported() {
  return Boolean(pickMime());
}

export class CallTranscriber {
  // streams: { agent, client, release } de WhatsAppCallAudio.recordingStreams()
  // upload({ speaker, offset, blob, ext }) => Promise
  constructor({ streams, upload }) {
    this.streams = streams;
    this.upload = upload;
    this.mime = pickMime();
    this.ext = this.mime.includes("ogg") ? "ogg" : "webm";
    this.recorders = [];
    this.work = [];
    this.failures = 0;
    this.timer = null;
    this.startedAt = 0;
  }

  start() {
    this.startedAt = performance.now();
    this.startSegment();
    this.timer = setInterval(() => this.rotate(), CHUNK_MS);
  }

  startSegment() {
    const offset = (performance.now() - this.startedAt) / 1000;
    this.recorders = [
      ["agent", this.streams.agent],
      ["client", this.streams.client],
    ]
      .filter(([, stream]) => stream)
      .map(([speaker, stream]) => {
        const recorder = new window.MediaRecorder(stream, {
          mimeType: this.mime,
          audioBitsPerSecond: BITRATE,
        });
        const parts = [];
        recorder.ondataavailable = (event) => {
          if (event.data?.size) parts.push(event.data);
        };
        const done = new Promise((resolve) => {
          recorder.onstop = resolve;
        });
        recorder.start();
        return { speaker, recorder, parts, done, offset };
      });
  }

  // Para os gravadores na hora (antes de qualquer await): chamado no fim da
  // ligação, precisa capturar o áudio antes de o contexto de áudio fechar.
  async flush(segment) {
    segment.forEach(({ recorder }) => {
      if (recorder.state !== "inactive") recorder.stop();
    });
    await Promise.all(segment.map(({ done }) => done));

    const type = this.mime.split(";")[0];
    await Promise.all(
      segment.map(async ({ speaker, parts, offset }) => {
        const blob = new Blob(parts, { type });
        if (blob.size < MIN_BYTES) return;
        try {
          await this.upload({ speaker, offset, blob, ext: this.ext });
        } catch {
          this.failures += 1;
        }
      }),
    );
  }

  rotate() {
    const finished = this.recorders;
    this.startSegment();
    this.work.push(this.flush(finished));
  }

  // Descarta sem enviar nada (o backend recusou ou a ligação já acabou).
  discard() {
    clearInterval(this.timer);
    this.recorders.forEach(({ recorder }) => {
      recorder.ondataavailable = null;
      if (recorder.state !== "inactive") recorder.stop();
    });
    this.recorders = [];
    this.streams.release?.();
  }

  async stop() {
    clearInterval(this.timer);
    const finished = this.recorders;
    this.recorders = [];
    this.work.push(this.flush(finished));
    try {
      await Promise.allSettled(this.work);
    } finally {
      this.streams.release?.();
    }
    return { failures: this.failures };
  }
}
